import { IUser } from '@my-backoffice/app/pages/user/shared/models/interfaces/user.interface';
import { AUTHENTICATION_SERVICE } from '@my-backoffice/app/shared/auth/authentication.token';
import { EBrokerType } from '@my-backoffice/app/shared/models/enums/broker-type.enum';
import { EBusinessUnit } from '@my-backoffice/app/shared/models/enums/business-unit.enum';
import { IReportRejectionCategory } from '@my-backoffice/app/shared/models/interfaces/report-rejection-category.interface';
import { ISelect } from '@my-backoffice/app/shared/models/interfaces/select.interface';
import { inject, Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class AppUserSettingService {
  private readonly _authenticationService = inject(AUTHENTICATION_SERVICE);

  private _userData: IUser = this._authenticationService.getCurrentUser();

  public constructor() {
    this._authenticationService.currentUserSubject$.pipe(untilDestroyed(this)).subscribe((user): void => {
      this._userData = user;
    });
  }

  public getDivisions(): ISelect[] {
    const { productDivisions } = this._userData.preferences.booking;
    const divisions: ISelect[] = [];

    if (this._userData.preferences.booking.showProductDivision) {
      productDivisions.split(';').forEach((item: string): void => {
        divisions.push({
          label: item,
          value: item,
        });
      });
    }

    return divisions;
  }

  public isSubAccount(): boolean {
    return !this._userData.company?.superMaster?.superMaster && this._userData.company?.type === 'subordinate';
  }

  public getUseQuickFormByDefault(): boolean {
    return this._userData.preferences.booking.useQuickFormByDefault;
  }

  public getUseCustomizedProductType(): boolean {
    return this._userData.preferences.booking.preferredProductFamilies.useCustomizedProductType;
  }

  public getClientCanApproveRejectIndividualProductReferences(): boolean {
    return this._userData.preferences.booking.multiReference.clientCanApproveRejectIndividualProductReferences;
  }

  public getIsPoMandatory(): boolean {
    return this._userData.preferences.booking.isPoMandatory;
  }

  public getIsTestCompany(): boolean {
    if (this._userData?.company?.isTestCompany) {
      return this._userData.company.isTestCompany === 'Y';
    }

    return false;
  }

  public getShouldSendRefSampleToFactory(): boolean {
    return this._userData.preferences.booking.shouldSendRefSampleToFactory;
  }

  public getNumberOfPcsPerRef(): number {
    return this._userData.preferences?.booking.multiReference.numberOfPcsPerRef || 999999999;
  }

  public getNumberOfRefPerReport(): number {
    return this._userData.preferences.booking.multiReference.numberOfRefPerReport;
  }

  public getNumberOfRefPerProduct(): number {
    return this._userData.preferences.booking.multiReference.numberOfRefPerProduct;
  }

  public getIsAskNumberOfReferences(): boolean {
    return this._userData.preferences.booking.multiReference.askNumberOfReferences;
  }

  public getBookOrdersWithMultipleFactories(): boolean {
    return this._userData.preferences.booking.bookOrdersWithMultipleFactories;
  }

  public getLimitSupplierInformation(): boolean {
    return this._userData.company.limitSupplierInformation;
  }

  public getCanSeeAllSuppliersAndFactories(): boolean {
    return this._userData.company.canSeeAllSuppliersAndFactories;
  }

  public getGuidedInspectionEnabled(): boolean {
    return this._userData.preferences?.booking.guidedInspectionEnabled;
  }

  public getSampleFee(): number {
    return this._userData.rate.sampleFee;
  }

  public getAllowCustomizedFinishedPackedSeparately(): boolean {
    return this._userData.preferences.booking.allowCustomizedFinishedPackedSeparately;
  }

  public getAllowEditFinishedPacked(): boolean {
    return this._userData.preferences.booking.allowEditFinishedPacked;
  }

  public getPsiMinReportsSizePerBooking(): number {
    return parseInt(this._userData.preferences.booking.minReportsSizePerBooking, 10);
  }

  public getPsiMinSampleSizePerBooking(): number {
    return parseInt(this._userData.preferences.booking.minSampleSizePerBooking, 10);
  }

  public getMinQuantityToBeReady(index: number): number {
    return this._userData.preferences.booking.minQuantityToBeReady[index].minQty;
  }

  public getMinPackedPctToBeReady(index: number): number {
    return this._userData.preferences.booking.minPackedPctToBeReady[index].minPct;
  }

  public getAutoSendIC(): boolean {
    return this._userData.preferences.report.autoSendIC;
  }

  public getReportRejectionCategories(): IReportRejectionCategory[] {
    return this._userData.preferences.report.rejectCategories;
  }

  public getAccessDashboardOnly(): boolean {
    return this._userData.company.superMaster.accessDashboardOnly;
  }

  public getIsLifeScience(): boolean {
    return this._userData.belongLifeScience === true;
  }

  public getIsQimaProduce(): boolean {
    return this._userData.businessUnit === EBusinessUnit.QIMA_PRODUCE;
  }

  public getBusinessUnit(): EBusinessUnit {
    return this._userData.businessUnit;
  }

  public getIsWqsEnabled(): boolean {
    return !!this._userData.preferences?.booking?.enableWqsBooking;
  }

  public getIsBrokerType(): boolean {
    return this._userData.company.brokerType === EBrokerType.BROKER;
  }
}
